import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/dialog";
import { Button } from "@/components/button";
import { useQueryGetGroupList } from "@/apis/groups/getGroupList";
import { useQueryGetUserList } from "@/apis/users/getUserList";
import { useQueryGetExamAssignmentMonitor } from "@/apis/exam_assignments/getExamAssignmentMonitor";
import { Checkbox } from "@/components/checkbox";
import { Input, InputGroup } from "@/components/input";
import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";

export interface AnalysisFilterState {
  filterType:
    | "none"
    | "exam_time_group"
    | "current_group"
    | "specific_students";
  examTimeGroupId?: string;
  currentGroupId?: string;
  selectedUserIds?: string[];
}

interface FilteringAnalysisModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: AnalysisFilterState) => void;
  currentFilters: AnalysisFilterState;
  examAssignmentId?: string;
}

const FilteringAnalysisModal: React.FC<FilteringAnalysisModalProps> = ({
  open,
  onClose,
  onApply,
  currentFilters,
  examAssignmentId,
}) => {
  const [tempFilters, setTempFilters] =
    useState<AnalysisFilterState>(currentFilters);
  const [studentSearch, setStudentSearch] = useState("");

  // Get group list for dropdowns
  const { data: groupsData } = useQueryGetGroupList(
    { limit: 100 },
    { enabled: open }
  );

  // Get exam assignment monitor data to get students who took the exam
  const { data: monitorData } = useQueryGetExamAssignmentMonitor(
    examAssignmentId || ""
  );

  // Get all students data for group filtering (fallback for older data)
  const { data: allStudentsData } = useQueryGetUserList(
    {
      page: 1,
      limit: 1000,
      role: "student",
      search: studentSearch,
    },
    { enabled: open && tempFilters.filterType === "specific_students" }
  );

  // Get students who took the exam from monitor data
  const examStudents =
    monitorData?.progress_list?.map((item) => ({
      id: item.user.id,
      name: item.user.name,
      email: item.user.email,
    })) || [];

  // Use exam students if available, otherwise fallback to all students
  // Only show students who took this specific exam when selecting specific students
  const studentsData =
    tempFilters.filterType === "specific_students" && examStudents.length > 0
      ? { items: examStudents }
      : allStudentsData;

  // Update tempFilters when modal opens with current filters
  useEffect(() => {
    if (open) {
      setTempFilters(currentFilters);
    }
  }, [open, currentFilters]);

  const handleFilterTypeChange = (
    filterType: AnalysisFilterState["filterType"]
  ) => {
    setTempFilters({
      filterType,
      // Reset other filter values when changing type
      examTimeGroupId: undefined,
      currentGroupId: undefined,
      selectedUserIds: [],
    });
  };

  const handleGroupSelect = (
    groupId: string,
    filterType: "exam_time_group" | "current_group"
  ) => {
    if (filterType === "exam_time_group") {
      setTempFilters((prev) => ({
        ...prev,
        examTimeGroupId: groupId,
      }));
    } else {
      setTempFilters((prev) => ({
        ...prev,
        currentGroupId: groupId,
      }));
    }
  };

  const handleStudentToggle = (userId: string) => {
    setTempFilters((prev) => ({
      ...prev,
      selectedUserIds: prev.selectedUserIds?.includes(userId)
        ? prev.selectedUserIds.filter((id) => id !== userId)
        : [...(prev.selectedUserIds || []), userId],
    }));
  };

  const handleSelectAllStudents = () => {
    if (!studentsData?.items) return;

    const allStudentIds = studentsData.items.map((student) => student.id);
    const allSelected = allStudentIds.every((id) =>
      tempFilters.selectedUserIds?.includes(id)
    );

    setTempFilters((prev) => ({
      ...prev,
      selectedUserIds: allSelected ? [] : allStudentIds,
    }));
  };

  const handleApply = () => {
    onApply(tempFilters);
    onClose();
  };

  const handleCancel = () => {
    setTempFilters(currentFilters);
    onClose();
  };

  const filteredStudents =
    studentsData?.items?.filter(
      (student) =>
        student.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
        student.email.toLowerCase().includes(studentSearch.toLowerCase())
    ) || [];

  return (
    <Dialog open={open} onClose={handleCancel} size="lg">
      <DialogTitle>Filter Analysis Report</DialogTitle>
      <DialogBody className="space-y-6">
        {/* Filter Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Filter Type (Select one)
          </label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                name="filterType"
                value="none"
                checked={tempFilters.filterType === "none"}
                onChange={() => handleFilterTypeChange("none")}
                className="mr-2"
              />
              Show All Students
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="filterType"
                value="exam_time_group"
                checked={tempFilters.filterType === "exam_time_group"}
                onChange={() => handleFilterTypeChange("exam_time_group")}
                className="mr-2"
              />
              Filter by Exam Time Group
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="filterType"
                value="current_group"
                checked={tempFilters.filterType === "current_group"}
                onChange={() => handleFilterTypeChange("current_group")}
                className="mr-2"
              />
              Filter by Current Group
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="filterType"
                value="specific_students"
                checked={tempFilters.filterType === "specific_students"}
                onChange={() => handleFilterTypeChange("specific_students")}
                className="mr-2"
              />
              Select Specific Students
            </label>
          </div>
        </div>

        {/* Exam Time Group Selection */}
        {tempFilters.filterType === "exam_time_group" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Exam Time Group
            </label>
            <select
              value={tempFilters.examTimeGroupId || ""}
              onChange={(e) =>
                handleGroupSelect(e.target.value, "exam_time_group")
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a group...</option>
              {groupsData?.items?.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Current Group Selection */}
        {tempFilters.filterType === "current_group" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Group
            </label>
            <select
              value={tempFilters.currentGroupId || ""}
              onChange={(e) =>
                handleGroupSelect(e.target.value, "current_group")
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a group...</option>
              {groupsData?.items?.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Specific Students Selection */}
        {tempFilters.filterType === "specific_students" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Students
            </label>
            <div className="text-sm text-gray-600 mb-3">
              {examStudents.length > 0
                ? `Showing ${examStudents.length} students who took this exam`
                : "Loading students who took this exam..."}
            </div>

            {/* Search Input */}
            <div className="mb-3">
              <InputGroup>
                <MagnifyingGlassIcon />
                <Input
                  placeholder="Search students..."
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                />
              </InputGroup>
            </div>

            {/* Select All Button */}
            <div className="mb-3">
              <Button onClick={handleSelectAllStudents} className="text-sm">
                {filteredStudents.every((student) =>
                  tempFilters.selectedUserIds?.includes(student.id)
                )
                  ? "Deselect All"
                  : "Select All"}
              </Button>
            </div>

            {/* Students List */}
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md p-2">
              {filteredStudents.length === 0 ? (
                <div className="text-gray-500 text-center py-4">
                  No students found
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredStudents.map((student) => (
                    <div
                      key={student.id}
                      className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded"
                      onClick={() => handleStudentToggle(student.id)}
                    >
                      <Checkbox
                        checked={
                          tempFilters.selectedUserIds?.includes(student.id) ||
                          false
                        }
                        onChange={() => handleStudentToggle(student.id)}
                        className="mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {student.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {student.email}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected count */}
            <div className="mt-2 text-sm text-gray-600">
              {tempFilters.selectedUserIds?.length || 0} student(s) selected
            </div>
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleApply}>Apply Filter</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilteringAnalysisModal;
